import type { Restaurant } from '../types/restaurant';

/**
 * 检查餐厅数据是否为真实数据（来自Google Places API）
 * 
 * @param restaurants 餐厅数据数组
 * @returns 检查结果
 */
export interface DataSourceCheckResult {
  isRealData: boolean;
  confidence: 'high' | 'medium' | 'low';
  indicators: {
    placeIdFormat: boolean; // ID是否为Google Place ID格式
    imageUrlFromProxy: boolean; // 图片URL是否来自后端代理
    addressFormat: boolean; // 地址格式是否为真实格式
    dataVariety: boolean; // 数据是否多样化（非固定数据）
  };
  details: {
    sampleIds: string[];
    sampleImageUrls: string[];
    sampleAddresses: string[];
  };
  recommendations: string[];
}

/**
 * 检查数据源
 */
export function checkDataSource(restaurants: Restaurant[]): DataSourceCheckResult {
  if (!restaurants || restaurants.length === 0) {
    return {
      isRealData: false,
      confidence: 'low',
      indicators: {
        placeIdFormat: false,
        imageUrlFromProxy: false,
        addressFormat: false,
        dataVariety: false,
      },
      details: {
        sampleIds: [],
        sampleImageUrls: [],
        sampleAddresses: [],
      },
      recommendations: ['无数据可检查'],
    };
  }

  const SUPABASE_PROJECT_REF = import.meta.env.VITE_SUPABASE_PROJECT_REF;
  const BACKEND_PROXY_URL = SUPABASE_PROJECT_REF 
    ? `https://${SUPABASE_PROJECT_REF}.supabase.co/functions/v1/maps-proxy`
    : '';
  const MOCK_IMAGE_PATTERNS = ['/assets/', '/public/assets/'];
  
  // 检查指标
  const sampleIds = restaurants.slice(0, 3).map(r => r.id);
  const sampleImageUrls = restaurants.slice(0, 3).map(r => r.image).filter(Boolean);
  const sampleAddresses = restaurants.slice(0, 3).map(r => r.address).filter(Boolean);

  // 1. 检查ID格式 - Google Place ID通常是长字符串（>20字符）
  const placeIdFormat = sampleIds.every(id => id && id.length > 20 && !/^\d+$/.test(id));
  
  // 2. 检查图片URL - 真实数据应该包含后端代理URL
  const imageUrlFromProxy = sampleImageUrls.some(url => 
    url && url.includes(BACKEND_PROXY_URL)
  );
  
  // 3. 检查地址格式 - 真实数据应该有详细的地址信息
  const addressFormat = sampleAddresses.some(addr => 
    addr && (addr.includes('都') || addr.includes('府') || addr.includes('県') || addr.includes(',') || addr.length > 20)
  );
  
  // 4. 检查数据多样性 - Mock数据通常是固定的10个餐厅
  // 真实数据应该根据搜索关键词变化
  const dataVariety = restaurants.length <= 10 ? 
    !isKnownMockData(restaurants) : true;
  
  // 计算置信度
  const trueCount = [placeIdFormat, imageUrlFromProxy, addressFormat, dataVariety].filter(Boolean).length;
  let confidence: 'high' | 'medium' | 'low';
  if (trueCount >= 3) {
    confidence = 'high';
  } else if (trueCount >= 2) {
    confidence = 'medium';
  } else {
    confidence = 'low';
  }
  
  // 判断是否为真实数据
  const isRealData = confidence === 'high' || (confidence === 'medium' && placeIdFormat && imageUrlFromProxy);
  
  // 生成建议
  const recommendations: string[] = [];
  if (!placeIdFormat) {
    recommendations.push('ID格式不符合Google Place ID格式（应该是长字符串）');
  }
  if (!imageUrlFromProxy) {
    recommendations.push('图片URL不包含后端代理地址，可能是本地mock数据');
  }
  if (!addressFormat) {
    recommendations.push('地址格式不完整，可能不是真实数据');
  }
  if (!dataVariety) {
    recommendations.push('数据可能是固定的mock数据');
  }
  if (isRealData) {
    recommendations.push('✅ 数据来源检查通过，确认使用真实数据');
  } else {
    recommendations.push('⚠️ 数据可能来自mock数据源，请检查API配置');
  }
  
  return {
    isRealData,
    confidence,
    indicators: {
      placeIdFormat,
      imageUrlFromProxy,
      addressFormat,
      dataVariety,
    },
    details: {
      sampleIds,
      sampleImageUrls: sampleImageUrls as string[],
      sampleAddresses: sampleAddresses as string[],
    },
    recommendations,
  };
}

/**
 * 检查是否为已知的mock数据
 */
function isKnownMockData(restaurants: Restaurant[]): boolean {
  const knownMockNames = [
    'Ichiran Ramen Shibuya',
    'Sukiyabashi Jiro',
    'Gonpachi Nishi-Azabu',
    'Tempura Kondo',
    'Yakiniku Jumbo Hanare',
    'Din Tai Fung Osaka',
    'Savoy Azabu',
    'Moti Indian Restaurant',
    'Pho Thin Tokyo',
    'Osaka Dotonbori Kamukura',
  ];
  
  const restaurantNames = restaurants.map(r => r.name);
  const matches = restaurantNames.filter(name => 
    knownMockNames.some(mockName => name.includes(mockName) || mockName.includes(name))
  );
  
  // 如果匹配度超过50%，可能是mock数据
  return matches.length > restaurants.length * 0.5;
}

/**
 * 在控制台输出检查结果
 */
export function logDataSourceCheck(restaurants: Restaurant[]): void {
  const result = checkDataSource(restaurants);
  
  if (import.meta.env.DEV) {
    console.group('📊 数据源检查结果');
    console.log('数据源:', result.isRealData ? '✅ 真实数据' : '❌ Mock数据');
    console.log('置信度:', result.confidence);
    console.log('');
    console.log('检查指标:');
    console.log('  - ID格式:', result.indicators.placeIdFormat ? '✅' : '❌');
    console.log('  - 图片URL:', result.indicators.imageUrlFromProxy ? '✅' : '❌');
    console.log('  - 地址格式:', result.indicators.addressFormat ? '✅' : '❌');
    console.log('  - 数据多样性:', result.indicators.dataVariety ? '✅' : '❌');
    console.log('');
    console.log('样本数据:');
    console.log('  - IDs:', result.details.sampleIds);
    console.log('  - 图片URLs:', result.details.sampleImageUrls);
    console.log('  - 地址:', result.details.sampleAddresses);
    console.log('');
    console.log('建议:');
    result.recommendations.forEach(rec => console.log('  -', rec));
    console.groupEnd();
  }
}

